import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useEffect, useState } from 'react';
import PageLoader from './components/PageLoader';
import OfflineScreen from './components/OfflineScreen';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ProtectedRoute from './components/ProtectedRoute';
import Home from './pages/Home';
import Sponsors from './pages/Sponsors';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Terms from './pages/Terms';
import Leaderboard from './pages/Leaderboard';
import Gallery from './pages/Gallery';
import PressRegistration from './pages/PressRegistration';
import Schedule from './pages/Schedule';
import About from './pages/About';
import Support from './pages/Support';
import Registration from './pages/Registration';
import History from './pages/History';
import Dashboard from './pages/Dashboard';
import TournamentDetails from './pages/TournamentDetails';
import CookieConsent from './components/CookieConsent';
import ScrollToTop from './components/ScrollToTop';
import Settings from './pages/Settings';
import NotFound from './pages/NotFound';
import AdminSetup from './pages/AdminSetup';
import AcceptInvite from './pages/AcceptInvite';
import PrizeBreakdown from './pages/PrizeBreakdown';
import InterestForm from './pages/InterestForm';
import Promo from './pages/Promo';
import Scorer from './pages/Scorer';
import MobileApp from './mobile/MobileApp';
import TabletApp from './tablet/TabletApp';
import TvDisplay from './tv/TvDisplay';
import { useGoogleAnalytics } from './hooks/useGoogleAnalytics';
import { useAnalyticsTracker } from './hooks/useAnalyticsTracker';
import { UAParser } from 'ua-parser-js';

function isMobileDevice() {
  const result = UAParser(navigator.userAgent);
  const deviceType = result.device.type;
  return deviceType === 'mobile' || window.innerWidth < 768;
}

function isTabletDevice() {
  const result = UAParser(navigator.userAgent);
  const deviceType = result.device.type;
  const isIpad = /Macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints > 1;
  const widthInRange = window.innerWidth >= 768 && window.innerWidth < 1200;
  return deviceType === 'tablet' || isIpad || widthInRange;
}

function DeviceRedirect() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const excluded = location.pathname.startsWith('/m')
      || location.pathname.startsWith('/t')
      || location.pathname.startsWith('/tv')
      || location.pathname.startsWith('/promo')
      || location.pathname.startsWith('/dashboard')
      || location.pathname.startsWith('/settings')
      || location.pathname.startsWith('/admin')
      || location.pathname.startsWith('/scorer');

    if (!excluded) {
      if (isMobileDevice()) {
        const mobilePath = location.pathname === '/invite' ? '/m/invite' : '/m';
        navigate(mobilePath, { replace: true });
      } else if (isTabletDevice()) {
        navigate('/t', { replace: true });
      }
    }
    // /tv is never auto-redirected
  }, []);

  return null;
}

function DesktopApp() {
  useGoogleAnalytics();
  useAnalyticsTracker();
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/admin-setup" element={<AdminSetup />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />
        <Route path="/sponsors" element={<Sponsors />} />
        <Route path="/partners" element={<Sponsors />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/leaderboard" element={<ProtectedRoute component={Leaderboard} pageKey="leaderboard" />} />
        <Route path="/prize-breakdown" element={<PrizeBreakdown />} />
        <Route path="/gallery" element={<ProtectedRoute component={Gallery} pageKey="gallery" />} />
        <Route path="/press" element={<ProtectedRoute component={PressRegistration} pageKey="press" />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/schedule" element={<ProtectedRoute component={Schedule} pageKey="schedule" />} />
        <Route path="/about" element={<About />} />
        <Route path="/support" element={<Support />} />
        <Route path="/registration" element={<Registration />} />
        <Route path="/invite" element={<InterestForm />} />
        <Route path="/interest" element={<Navigate to="/invite" replace />} />
        <Route path="/promo" element={<Promo />} />
        <Route path="/history" element={<ProtectedRoute component={History} pageKey="history" />} />
        <Route path="/dashboard/*" element={<Dashboard />} />
        <Route path="/scorer/*" element={<Scorer />} />
        <Route path="/tournaments/:id" element={<TournamentDetails />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      <Footer />
      <CookieConsent />
      <ScrollToTop />
    </div>
  );
}

function App() {
  const [appReady, setAppReady] = useState(false);

  return (
    <Router>
      <OfflineScreen />
      {!appReady && <PageLoader onComplete={() => setAppReady(true)} />}
      <DeviceRedirect />
      <Routes>
        <Route path="/m/*" element={<MobileApp />} />
        <Route path="/t/*" element={<TabletApp />} />
        <Route path="/tv" element={<TvDisplay />} />
        <Route path="*" element={<DesktopApp />} />
      </Routes>
      <Toaster position="top-right" toastOptions={{ duration: 4000 }} />
    </Router>
  );
}

export default App;
